(() => {
  if (customElements.get('renao-magnet-location')) return;
  // Reuse the Google Places service used by the existing ReNao map products.
  const loadPlaces = (key) => {
    if (window.google?.maps?.importLibrary) return google.maps.importLibrary('places');
    if (!window.__renaoMagnetPlacesPromise) {
      window.__renaoMagnetPlacesPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const fail = () => { script.remove(); reject(new Error('places-unavailable')); };
        const timer = setTimeout(fail, 15000);
        window.__renaoMagnetPlacesLoaded = () => {
          clearTimeout(timer);
          google.maps.importLibrary('places').then(resolve, reject);
        };
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&language=tr&region=TR&loading=async&callback=__renaoMagnetPlacesLoaded`;
        script.async = true;
        script.onerror = () => { clearTimeout(timer); fail(); };
        document.head.appendChild(script);
      }).catch(error => { window.__renaoMagnetPlacesPromise = null; throw error; });
    }
    return window.__renaoMagnetPlacesPromise;
  };
  customElements.define('renao-magnet-location', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.run = 0;
      this.input = this.querySelector('[data-location-search]');
      this.results = this.querySelector('[data-location-results]');
      this.status = this.querySelector('[data-location-status]');
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.input.addEventListener('input', () => {
        this.clearSelection();
        const run = ++this.run;
        clearTimeout(this.timer);
        this.hideResults();
        const query = this.input.value.trim();
        this.setStatus('');
        if (query.length < 3) return;
        this.timer = setTimeout(() => this.search(query, run), 250);
      }, options);
      this.input.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' && !this.results.hidden) {
          event.preventDefault(); this.results.querySelector('button')?.focus();
        } else if (event.key === 'Escape') this.hideResults();
        else if (event.key === 'Enter') event.preventDefault();
      }, options);
      this.results.addEventListener('keydown', event => {
        const buttons = [...this.results.querySelectorAll('button')];
        const index = buttons.indexOf(document.activeElement);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); buttons[(index + (event.key === 'ArrowDown' ? 1 : buttons.length - 1)) % buttons.length]?.focus();
        } else if (event.key === 'Escape') { this.hideResults(); this.input.focus(); }
      }, options);
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget)) this.hideResults();
      }, options);
    }
    disconnectedCallback() {
      clearTimeout(this.timer); this.run++; this.controller?.abort(); this.ready = false;
    }
    isConfirmed() {
      return Boolean(this.selected && this.input.value === this.selected && this.querySelector('[data-location-lat]').value && this.querySelector('[data-location-lng]').value);
    }
    setStatus(message) { this.status.textContent = message; this.status.hidden = !message; }
    hideResults() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
    clearSelection() {
      this.selected = '';
      this.querySelector('[data-location-badge]').hidden = true;
      this.querySelectorAll('input[type=hidden]').forEach(input => { input.value = ''; });
    }
    async search(query, run) {
      this.setStatus('Konum aranıyor…');
      try {
        const places = await loadPlaces(this.dataset.googleKey);
        this.session ||= new places.AutocompleteSessionToken();
        const {suggestions} = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({input: query, sessionToken: this.session, language: 'tr', region: 'TR'});
        if (run !== this.run || !this.isConnected) return;
        const predictions = suggestions.map(item => item.placePrediction).filter(Boolean).slice(0, 5);
        this.results.replaceChildren();
        predictions.forEach((prediction, index) => {
          const button = document.createElement('button');
          button.type = 'button'; button.setAttribute('role', 'option');
          button.id = `${this.input.id}-option-${index}`;
          const title = document.createElement('strong');
          title.textContent = prediction.mainText?.toString() || prediction.text.toString();
          const detail = document.createElement('small');
          detail.textContent = prediction.secondaryText?.toString() || '';
          button.append(title, detail);
          button.addEventListener('click', () => this.select(prediction, run));
          this.results.append(button);
        });
        this.results.hidden = !predictions.length;
        this.input.setAttribute('aria-expanded', String(Boolean(predictions.length)));
        this.setStatus(predictions.length ? '' : 'Konum bulunamadı. Daha detaylı ara.');
      } catch (error) {
        if (run === this.run) this.setStatus('Konum servisine ulaşılamadı. Tekrar ara.');
      }
    }
    async select(prediction, run) {
      this.hideResults(); this.setStatus('Konum seçiliyor…');
      try {
        const place = prediction.toPlace();
        await place.fetchFields({fields: ['location', 'formattedAddress', 'displayName']});
        if (run !== this.run || !this.isConnected) return;
        const lat = place.location?.lat(), lng = place.location?.lng();
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('no-coordinates');
        this.selected = prediction.text.toString();
        this.input.value = this.selected;
        this.querySelector('[data-location-lat]').value = lat.toFixed(7);
        this.querySelector('[data-location-lng]').value = lng.toFixed(7);
        this.querySelector('[data-location-coordinates]').value = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        this.querySelector('[data-location-place]').value = prediction.placeId;
        this.querySelector('[data-location-badge]').hidden = false;
        this.session = null; this.setStatus(''); this.input.focus();
        this.input.dispatchEvent(new Event('change', {bubbles: true}));
      } catch (error) {
        if (run === this.run) { this.clearSelection(); this.setStatus('Konum alınamadı. Tekrar seç.'); }
      }
    }
  });
})();
